/**
 * date-format.ts — Pure date/display utility functions.
 *
 * Extracted from App.tsx to improve testability and reuse.
 * All functions are pure (no side effects, no external dependencies).
 */

// ---------------------------------------------------------------------------
// Month normalisation
// ---------------------------------------------------------------------------

/** Ordered list of month patterns (longer names first to avoid partial matches) */
const MONTH_PATTERNS: [RegExp, string][] = [
  // Full French names
  [/\bjanvier\b/gi, 'Janvier'],
  [/\bfévrier\b/gi, 'Février'],
  [/\bfevrier\b/gi, 'Février'],
  [/\bmars\b/gi, 'Mars'],
  [/\bavril\b/gi, 'Avril'],
  [/\bmai\b/gi, 'Mai'],
  [/\bjuin\b/gi, 'Juin'],
  [/\bjuillet\b/gi, 'Juillet'],
  [/\baoût\b/gi, 'Août'],
  [/\baout\b/gi, 'Août'],
  [/\bseptembre\b/gi, 'Septembre'],
  [/\boctobre\b/gi, 'Octobre'],
  [/\bnovembre\b/gi, 'Novembre'],
  [/\bdécembre\b/gi, 'Décembre'],
  [/\bdecembre\b/gi, 'Décembre'],
  // Abbreviations with dot
  [/\bjanv\./gi, 'Janvier'],
  [/\bjan\./gi, 'Janvier'],
  [/\bfévr\./gi, 'Février'],
  [/\bfév\./gi, 'Février'],
  [/\bfev\./gi, 'Février'],
  [/\bavr\./gi, 'Avril'],
  [/\bjuil\./gi, 'Juillet'],
  [/\bsept\./gi, 'Septembre'],
  [/\boct\./gi, 'Octobre'],
  [/\bnov\./gi, 'Novembre'],
  [/\bdéc\./gi, 'Décembre'],
  [/\bdec\./gi, 'Décembre'],
  // Abbreviations without dot
  [/\bjanv\b/gi, 'Janvier'],
  [/\bjan\b/gi, 'Janvier'],
  [/\bfévr\b/gi, 'Février'],
  [/\bfév\b/gi, 'Février'],
  [/\bfev\b/gi, 'Février'],
  [/\bfeb\b/gi, 'Février'],
  [/\bmar\b/gi, 'Mars'],
  [/\bavr\b/gi, 'Avril'],
  [/\bapr\b/gi, 'Avril'],
  [/\bmay\b/gi, 'Mai'],
  [/\bjun\b/gi, 'Juin'],
  [/\bjuil\b/gi, 'Juillet'],
  [/\bjul\b/gi, 'Juillet'],
  [/\baug\b/gi, 'Août'],
  [/\bsept\b/gi, 'Septembre'],
  [/\bsep\b/gi, 'Septembre'],
  [/\boct\b/gi, 'Octobre'],
  [/\bnov\b/gi, 'Novembre'],
  [/\bdéc\b/gi, 'Décembre'],
  [/\bdec\b/gi, 'Décembre'],
];

const FR_TO_EN_MONTHS: Record<string, string> = {
  Janvier: 'January', Février: 'February', Mars: 'March', Avril: 'April',
  Mai: 'May', Juin: 'June', Juillet: 'July', Août: 'August',
  Septembre: 'September', Octobre: 'October', Novembre: 'November', Décembre: 'December',
  Présent: 'Present',
};

/**
 * Normalise month names/abbreviations to their full capitalised form.
 * Internally always normalises to French, then converts to English if outputLocale is 'en'.
 *
 * @example
 * formatDateWithFullMonth('jan. 2022') // → 'Janvier 2022'
 * formatDateWithFullMonth('jan. 2022', 'en') // → 'January 2022'
 * formatDateWithFullMonth('présent', 'en') // → 'Present'
 */
export function formatDateWithFullMonth(dateStr: string, outputLocale?: 'fr' | 'en'): string {
  if (!dateStr) return '';

  if (/présent|present|actuel/i.test(dateStr)) {
    return outputLocale === 'en' ? 'Present' : 'Présent';
  }

  let formatted = dateStr;
  for (const [regex, replacement] of MONTH_PATTERNS) {
    formatted = formatted.replace(regex, replacement);
  }

  if (outputLocale === 'en') {
    for (const [fr, en] of Object.entries(FR_TO_EN_MONTHS)) {
      formatted = formatted.replace(new RegExp(fr, 'g'), en);
    }
  }

  return formatted;
}

// ---------------------------------------------------------------------------
// Age calculation
// ---------------------------------------------------------------------------

const FRENCH_MONTHS: Record<string, number> = {
  janvier: 0, février: 1, mars: 2, avril: 3, mai: 4, juin: 5,
  juillet: 6, août: 7, septembre: 8, octobre: 9, novembre: 10, décembre: 11,
};

/**
 * Calculate age from a birth date string.
 * Supports French format ("28 décembre 1986"), ISO format (YYYY-MM-DD), and others.
 * Returns null if the string cannot be parsed.
 */
export function calculateAge(birthDateStr: string): number | null {
  if (!birthDateStr) return null;

  let birthDate: Date;

  const parts = birthDateStr.toLowerCase().split(' ');
  if (parts.length >= 3) {
    const day = parseInt(parts[0] ?? '');
    const month = FRENCH_MONTHS[parts[1] ?? ''];
    const year = parseInt(parts[parts.length - 1] ?? '');
    if (!isNaN(day) && month !== undefined && !isNaN(year)) {
      birthDate = new Date(year, month, day);
    } else {
      birthDate = new Date(birthDateStr);
    }
  } else {
    birthDate = new Date(birthDateStr);
  }

  if (isNaN(birthDate.getTime())) return null;

  const today = new Date();
  let age = today.getFullYear() - birthDate.getFullYear();
  const m = today.getMonth() - birthDate.getMonth();
  if (m < 0 || (m === 0 && today.getDate() < birthDate.getDate())) age--;
  return age;
}

// ---------------------------------------------------------------------------
// Text helpers
// ---------------------------------------------------------------------------

/**
 * Extract the last comma-separated part of a string (e.g. city from "Acme Corp, Paris").
 * Returns empty string if no comma found.
 */
export function extractCityFromCompany(company: string): string {
  const match = company.match(/,\s*([^,]+)$/);
  return match?.[1]?.trim() ?? '';
}

/**
 * Return "Né" or "Née" based on gender/civility fields.
 */
export function getBornPrefix(gender?: string, civility?: string): 'Né' | 'Née' {
  const g = gender?.toLowerCase() ?? '';
  if (g.includes('femme') || civility === 'Mme.') return 'Née';
  return 'Né';
}
